/**
 * Wastage Service - Using Redis session-based business context
 * No hardcoded business_id parameters
 */
import http from './httpClient';
import { postWithLockedRetry } from '../utils/ccsLockedRetry';
import { emitWastageCreated } from '../events/wastageEvents';

class WastageService {
  // Record wastage
  static async recordWastage(wastageData) {
    try {
      // A Locked month (continuous closing stock) offers a prior-month adjustment.
      const data = await postWithLockedRetry('/wastage', wastageData);
      if (!data.success) throw new Error(data.error || 'Failed to record wastage');
      // Notify listeners (Dashboard) that a new wastage record was added
      emitWastageCreated({
        stock_out_id: data?.data?.stock_out_id,
        estimated_cost: data?.data?.estimated_cost,
        created_at: Date.now()
      });
      return data;
    } catch (error) {
      console.error('Error recording wastage:', error);
      throw error;
    }
  }

  // Get wastage records - business_id from session
  static async getWastageRecords(startDate = null, endDate = null, limit = 50) {
    try {
      const params = new URLSearchParams({ limit });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const data = await http.get(`/wastage?${params.toString()}`);
      if (!data.success) throw new Error(data.error || 'Failed to fetch wastage records');
      return data;
    } catch (error) {
      console.error('Error fetching wastage records:', error);
      throw error;
    }
  }

  // Get wastage reasons - business_id from session
  static async getWastageReasons() {
    try {
      const data = await http.get(`/wastage/reasons`);
      if (!data.success) throw new Error(data.error || 'Failed to fetch wastage reasons');
      return data;
    } catch (error) {
      console.error('Error fetching wastage reasons:', error);
      throw error;
    }
  }

  // Get wastage summary - business_id from session
  static async getWastageSummary(startDate = null, endDate = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const data = await http.get(`/wastage/summary?${params.toString()}`);
      if (!data.success) throw new Error(data.error || 'Failed to fetch wastage summary');
      return data;
    } catch (error) {
      console.error('Error fetching wastage summary:', error);
      throw error;
    }
  }

  // Create a wastage reason - business_id from session
  static async createWastageReason({ reason_label, reason_category = 'General' }) {
    try {
      const data = await http.post('/wastage/reasons', { reason_label, reason_category });
      if (!data.success) throw new Error(data.error || 'Failed to create wastage reason');
      return data;
    } catch (error) {
      console.error('Error creating wastage reason:', error);
      throw error;
    }
  }

  // Delete (soft delete) a wastage reason
  static async deleteWastageReason(reasonId) {
    try {
      const data = await http.delete(`/wastage/reasons/${reasonId}`);
      if (!data.success) throw new Error(data.error || 'Failed to delete wastage reason');
      return data;
    } catch (error) {
      console.error('Error deleting wastage reason:', error);
      throw error;
    }
  }
}

/**
 * Upload photos for a waste record
 * @param {Array} photos - Array of photo objects with file property
 * @param {number} stockOutId - ID of the stock out record to associate photos with
 * @returns {Promise} - Promise resolving to the upload result
 */
export const uploadWastagePhotos = async (photos, stockOutId) => {
  try {
    // T1.7: argument-order guard.
    //
    // StockOutForm's administrative ("No Wastage Recorded") path called this as
    // uploadWastagePhotos(stockOutId, photos) — arguments reversed. `photos` was then a
    // number, `photos.length` was undefined, the old `!photos || photos.length === 0`
    // test passed it straight through, and `photos.forEach` threw a TypeError that the
    // caller's catch only console.error'd. Evidence photos were silently never uploaded.
    //
    // The call site is fixed, but the contract is enforced here too so the next caller
    // that gets it wrong fails loudly instead of silently.
    if (photos !== undefined && photos !== null && !Array.isArray(photos)) {
      throw new TypeError(
        `uploadWastagePhotos(photos, stockOutId): expected an array of photos, received ${typeof photos}. ` +
        'Check the argument order at the call site.'
      );
    }

    if (!photos || photos.length === 0) {
      return { success: true, message: 'No photos to upload', data: [] };
    }

    const formData = new FormData();

    // Add each photo to the form data
    photos.forEach((photo, index) => {
      if (!photo || !photo.file) {
        throw new TypeError(`uploadWastagePhotos: photo at index ${index} has no .file`);
      }
      formData.append('photos', photo.file, photo.file.name);
    });

    // Add stock_out_id if provided
    if (stockOutId) {
      formData.append('stock_out_id', stockOutId);
    }

    const data = await http.postFormData('/wastage/photos/upload', formData);
    return data;
  } catch (error) {
    console.error('Error uploading wastage photos:', error);
    throw error;
  }
};

/**
 * Get photos for a specific waste record
 * @param {number} stockOutId - ID of the stock out record
 * @returns {Promise} - Promise resolving to the photos data
 */
export const getWastagePhotos = async (stockOutId) => {
  try {
    const data = await http.get(`/wastage/photos/${stockOutId}`);
    return data;
  } catch (error) {
    console.error(`Error getting photos for waste record ${stockOutId}:`, error);
    throw error;
  }
};

export default WastageService;